import React from 'react';
import {
    ContactSupportModal as CoreContactSupportModal,
    NotificationType,
} from '@instinet-analytics/semantic-ui-components';
import { useDispatchSupportRequestAction } from './store/dispatcher';
import {
    useLoginStateSelector,
    useSelectUserHasAuthority,
} from 'login/store/selectors';
import { LoginStatus } from 'login/store/LoginState';
import { useReloadRequired } from 'embedded/api/hooks/useReloadRequired';
import { useDispatchShowNotification } from 'notification/store/dispatcher';
import { useSupportVisualisationPopupParent } from 'widgets/supportVisualisation/SupportVisualisationPopup/SupportVisualisationPopupService';
import { NewportAppVersionWarning } from 'newport/NewportAppVersionWarning';
import { useDisplayingNewportVersionWarning } from 'newport/store/hooks';
import classNames from 'classnames';

import './ContactSupportModal.scss';
import { OutdatedIndigoVersion } from 'app/OutdatedIndigoVersion/OutdatedIndigoVersion';
import { useDisplayingOutdatedIndigoVersionWarning } from 'app/OutdatedIndigoVersion/hooks';

const ShowForDevTeamAuthority = 'indigo.enableContactSupportDevMode';

export interface ContactSupportModalProps {
    className?: string;
    light?: boolean;
    trigger?: JSX.Element;
}
export default function ContactSupportModal(props: ContactSupportModalProps) {
    const { className, light, trigger } = props;
    const { onReloadClicked } = useReloadRequired();
    const dispatchSupportRequest = useDispatchSupportRequestAction();
    const dispatchNotification = useDispatchShowNotification();
    const displayingNewportVersionWarning =
        useDisplayingNewportVersionWarning();
    const displayingIndigoVersionMismatchWarning =
        useDisplayingOutdatedIndigoVersionWarning();

    const showForDevTeam = useSelectUserHasAuthority(ShowForDevTeamAuthority);

    const { status: loginStatus } = useLoginStateSelector();
    const needName = loginStatus !== LoginStatus.SUCCESS;
    const popupParent = useSupportVisualisationPopupParent();

    const onSendClicked = (
        enquiryText: string,
        name: string | undefined,
        screenshot: string,
        forDevTeam?: boolean
    ) => {
        if (enquiryText === 's-sv') {
            popupParent!.openPopup();
            return;
        }
        dispatchSupportRequest(
            enquiryText,
            needName ? name : undefined,
            screenshot,
            forDevTeam
        );
    };

    const showWarning = (message: string, title?: string) => {
        dispatchNotification(message, NotificationType.Warning, title);
    };

    const cssClasses = classNames(
        className,
        displayingNewportVersionWarning && 'newport-version-warning',
        displayingIndigoVersionMismatchWarning && 'indigo-mismatch-warning'
    );

    return (
        <CoreContactSupportModal
            className={cssClasses}
            light={light}
            needName={needName}
            prefix={
                <>
                    <OutdatedIndigoVersion />
                    <NewportAppVersionWarning showRecommended={true} />
                </>
            }
            showForDevTeam={showForDevTeam}
            trigger={trigger}
            onReloadRequested={onReloadClicked}
            onSendClicked={onSendClicked}
            showWarning={showWarning}
        />
    );
}
